import { DependantConfirmPage } from '@features/employee/pages/dependant-confirm/dependant-confirm.page';
import { Routes } from '@angular/router';
import { Home } from './pages/home/home';
import { EmptyPage } from './pages/empty-page/empty-page';
import { PermissionGuard } from '@core/guards/auth-guard';
import { ProfilePage } from './pages/profile/profile.page';
import { UserManagementListPage } from '@features/user-management/pages/user-management-list/user-management-list.page';
import { UserManagementCreatePage } from '@features/user-management/pages/user-management-create/user-management-create.page';
import { UserManagementDetailPage } from '@features/user-management/pages/user-management-detail/user-management-detail.page';
import { EmployeeListPage } from '@features/employee/pages/employee-list/employee-list.page';
import { EmployeeCreatePage } from '@features/employee/pages/employee-create/employee-create.page';
import { EmployeeDetailPage } from '@features/employee/pages/employee-detail/employee-detail.page';
import { EmployeeImportPage } from '@features/employee/pages/employee-import/employee-import.page';
import { ClientListPage } from '@features/client/pages/client-list/client-list.page';
import { ClientDetailPage } from '@features/client/pages/client-detail/client-detail.page';
import { ClientCreatePage } from '@features/client/pages/client-create/client-create.page';
import { CompanyListPage } from '@features/company/pages/company-list/company-list.page';
import { CompanyDetailPage } from '@features/company/pages/company-detail/company-detail.page';
import { CompanyCreatePage } from '@features/company/pages/company-create/company-create.page';
import { AffiliateListPage } from '@features/affiliate/pages/affiliate-list/affiliate-list.page';
import { AffiliateCreatePage } from '@features/affiliate/pages/affiliate-create/affiliate-create.page';
import { AffiliateDetailPage } from '@features/affiliate/pages/affiliate-detail/affiliate-detail.page';
import { InterventionListPage } from '@features/intervention/shell/pages/intervention-list/intervention-list.page';
import { InterventionCreatePage } from '@features/intervention/new-request/pages/intervention-create/intervention-create.page';
import { InterventionDetailPage } from '@features/intervention/shell/pages/intervention-detail/intervention-detail.page';
import { SessionNotePage } from '@features/intervention/delivery/pages/session-note/session-note.page';
import { RiskScreenerPage } from '@features/intervention/assessment/risk-screening/pages/risk-screener/risk-screener.page';
import { Permission } from '@shared/enums/permission.enum';

export const routes: Routes = [
    { path: '', redirectTo: 'users/profile', pathMatch: 'full' },
    {
        path: 'home',
        component: Home,
    },
    {
        path: 'users/profile',
        component: ProfilePage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.UsersRead },
    },
    {
        path: 'user-management/users',
        component: UserManagementListPage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.UsersManagementRead },
    },
    {
        path: 'user-management/users/new',
        component: UserManagementCreatePage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.UsersManagementCreate },
    },
    {
        path: 'user-management/users/:userId',
        component: UserManagementDetailPage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.UsersManagementRead },
    },
    {
        path: 'employees',
        component: EmployeeListPage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.EmployeesRead },
    },
    {
        path: 'employees/new',
        component: EmployeeCreatePage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.EmployeesCreate },
    },
    {
        path: 'employees/imports',
        component: EmployeeImportPage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.EmployeesCreate },
    },
    {
        path: 'employees/import',
        redirectTo: 'employees/imports',
        pathMatch: 'full',
    },
    {
        path: 'employees/:employeeId/dependants/:dependantId/confirm',
        component: DependantConfirmPage,
    },
    {
        path: 'employees/:employeeId',
        component: EmployeeDetailPage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.EmployeesRead },
    },
    {
        path: 'clients',
        component: ClientListPage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.ClientsRead },
    },
    {
        path: 'clients/new',
        component: ClientCreatePage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.ClientsCreate },
    },
    {
        path: 'clients/:clientId',
        component: ClientDetailPage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.ClientsRead },
    },
    {
        path: 'companies',
        component: CompanyListPage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.CompaniesRead },
    },
    {
        path: 'companies/new',
        component: CompanyCreatePage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.CompaniesCreate },
    },
    {
        path: 'companies/:companyId',
        component: CompanyDetailPage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.CompaniesRead },
    },
    {
        path: 'affiliates',
        component: AffiliateListPage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.AffiliatesRead },
    },
    {
        path: 'affiliates/new',
        component: AffiliateCreatePage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.AffiliatesCreate },
    },
    {
        path: 'affiliates/:affiliateId',
        component: AffiliateDetailPage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.AffiliatesRead },
    },
    {
        path: 'interventions',
        component: InterventionListPage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.InterventionsRead },
    },
    {
        path: 'interventions/new',
        component: InterventionCreatePage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.InterventionsUpdate },
    },
    {
        path: 'interventions/:id/risk-screeners/new',
        component: RiskScreenerPage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.RiskScreenerCreate },
    },
    {
        path: 'interventions/:id/risk-screeners/:screenerId',
        component: RiskScreenerPage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.RiskScreenerRead },
    },
    {
        path: 'interventions/:id/sessions/:sessionId/notes',
        component: SessionNotePage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.InterventionsRead },
    },
    {
        path: 'interventions/:id',
        component: InterventionDetailPage,
        canActivate: [PermissionGuard],
        data: { permission: Permission.InterventionsRead },
    },
    { path: '**', component: EmptyPage }
];
